class CollectionLoadAll extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.totalPages = parseInt(this.dataset.totalPages, 10) || 1;
    this.nextPage = parseInt(this.dataset.nextPage, 10) || 2;
    this.retryCount = 0;
    this.isFetching = false;

    this.target = this.resolveTarget();

    if (!this.target || this.nextPage > this.totalPages) {
      this.finish();
      return;
    }

    this.controller = new AbortController();

    this.setAttribute('data-loading', '');
    this.loadNextPage();
  }

  disconnectedCallback() {
    if (this.controller) this.controller.abort();
    if (this.retryTimer) window.clearTimeout(this.retryTimer);
  }

  resolveTarget() {
    const scoped = document.querySelector('#main-collection-product-grid [data-js-grid-target]');
    if (scoped) return scoped;
    const fallbacks = document.querySelectorAll('#main-collection-product-grid .product-grid-border-fix');
    return fallbacks.length ? fallbacks[fallbacks.length - 1] : null;
  }

  buildUrl(page) {
    const params = new URLSearchParams(window.location.search);
    params.set('section_id', this.sectionId);
    params.set('page', String(page));
    return `${window.location.pathname}?${params.toString()}`;
  }

  async loadNextPage() {
    if (!this.isConnected || this.isFetching) return;
    if (this.hasAttribute('data-complete') || this.hasAttribute('data-error')) return;

    if (this.nextPage > this.totalPages) {
      this.finish();
      return;
    }

    const pageToLoad = this.nextPage;
    this.isFetching = true;

    try {
      const response = await fetch(this.buildUrl(pageToLoad), { signal: this.controller.signal });
      if (!response.ok) throw new Error(`Request failed with ${response.status}`);

      const html = await response.text();
      const markup = this.extractProducts(html);

      if (!markup) {
        this.finish();
        return;
      }

      if (!this.isConnected) return;

      this.appendMarkup(markup, pageToLoad);
      this.retryCount = 0;
      this.nextPage = pageToLoad + 1;

      if (this.nextPage > this.totalPages) {
        this.finish();
        return;
      }

      window.setTimeout(() => this.loadNextPage(), 0);
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.retry();
    } finally {
      this.isFetching = false;
    }
  }

  retry() {
    if (this.retryCount >= 2) {
      this.removeAttribute('data-loading');
      this.setAttribute('data-error', '');
      return;
    }

    const backoff = this.retryCount === 0 ? 800 : 1600;
    this.retryCount += 1;
    this.retryTimer = window.setTimeout(() => this.loadNextPage(), backoff);
  }

  extractProducts(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const boxes = doc.querySelectorAll('#main-collection-product-grid .product-grid-border-fix > .site-box');

    return Array.from(boxes)
      .filter((box) => box.querySelector('.product-holder'))
      .map((box) => box.outerHTML)
      .join('');
  }

  appendMarkup(markup, page) {
    this.target.insertAdjacentHTML('beforeend', markup);

    const count = this.target.querySelectorAll(':scope > .site-box').length;
    this.target.dataset.count = String(count % 4);

    this.dispatchEvent(new CustomEvent('collection:page-appended', {
      bubbles: true,
      detail: { page, totalPages: this.totalPages }
    }));
  }

  finish() {
    this.removeAttribute('data-loading');
    this.setAttribute('data-complete', '');
  }
}

if (!customElements.get('collection-load-all')) {
  customElements.define('collection-load-all', CollectionLoadAll);
}
